import { DateTime } from 'luxon';
import { photos } from 'config/photos';
import { CarModel, VehicleType } from 'models/StockModel';
import { VoyageModel, VoyageStatus } from 'models/VoyageModel';
import { dataURLtoFile } from 'utils/common';


export const calcReadiness = (car: CarModel) => {
    let total = 20;
    let count = 0;

    car.exportMethod && count++;
    car.exportType && count++;
    car.vehicleType && count++;
    car.make && count++;
    car.model && count++;
    car.year && count++;
    car.fuel && count++;
    car.engineSize && count++;
    car.unitPrice && count++;
    car.priceTerms && count++;
    car.netWeight && count++;
    car.length && count++;
    car.width && count++;
    car.height && count++;
    car.photoCount && count++;
    car.partiesFilled && count++;
    car.parties.consignee && count++;
    car.yard && count++;
    car.yard?.arrived && count++;

    (car.documents.exportCertificate || car.documents.vehicleInvoice) && count++;

    // TODO check for exportCertOriginalSentAt if port agent requires

    priceConfirmationRequired(car) && !car.documents.priceConfirmation && count--;
    car.damaged && (!car.documents.repairEstimate || !car.documents.explanatoryNote) && count--;

    return count / total * 100;
};

export const resizeImage = (file: File) => {
    return new Promise<File>((resolve, reject) => {
        if (!file.type.match('image.*')) {
            reject(new Error('File is not an image'));
            return;
        }

        const img = new Image();

        img.onload = function () {
            let width = img.width;
            let height = img.height;

            if (width > height) {
                if (width > photos.largerSide) {
                    height *= photos.largerSide / width;
                    width = photos.largerSide;
                } else if (height > photos.smallerSide) {
                    width *= photos.smallerSide / height;
                    height = photos.smallerSide;
                }
            } else {
                if (height > photos.largerSide) {
                    width *= photos.largerSide / height;
                    height = photos.largerSide;
                } else if (width > photos.smallerSide) {
                    height *= photos.smallerSide / width;
                    width = photos.smallerSide;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx!.drawImage(img, 0, 0, width, height);

            // Convert the canvas to a data URL and return it
            const dataURL = file.type === 'image/jpeg' ?
                canvas.toDataURL('image/jpeg', photos.compression) :
                canvas.toDataURL(file.type);

            resolve(dataURLtoFile(dataURL, file.name));
        };

        const reader = new FileReader();

        reader.onload = function (event) {
            img.src = event.target!.result as string;
        };

        reader.readAsDataURL(file);
    });
};

const noECVehicles = [
    VehicleType.SpecialEquipment,
    VehicleType.Excavator,
    VehicleType.Tractor,
    VehicleType.Roller,
    VehicleType.Crane,
    VehicleType.Boat,
    VehicleType.Forklift,
];

export const exportCertificateRequired = (car: CarModel) => !car.vehicleType || !noECVehicles.includes(car.vehicleType);

export const priceConfirmationRequired = (car: CarModel) => (car.unitPrice && car.unitPrice >= 4000000) || car.vehicleType === VehicleType.Bike;

export const ecExpired = (car: CarModel, voyage?: VoyageModel) =>
    Boolean((car.voyage || voyage)?.status === VoyageStatus.Pending &&
        car.exportCertificate?.validUntil &&
        DateTime.fromISO(car.exportCertificate.validUntil) < DateTime.now().plus({days: 5}));

export const originalECRequired = (car: CarModel) => car.yard?.portId === 1 && !car.exportCertificate?.originalArrivedAt;
